import { formatTime } from "@clipboard-health/date-time";
import { patch } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { useCurrentUserId } from "../../lib/useCurrentUserId";
import {
  type ResponseWithError,
  type UpdateVisitRequest,
  updateVisitRequestSchema,
  type Visit,
  visitResponseSchema,
} from "../types";
import { getCaseVisitsQueryKey } from "./useCaseVisits";
import { getVisitQueryKey } from "./useVisit";

// Input type that expects Date instead of string for estimatedStartTime
type UpdateVisitInput = Omit<UpdateVisitRequest, "data"> & {
  data: Omit<UpdateVisitRequest["data"], "attributes"> & {
    attributes: Omit<UpdateVisitRequest["data"]["attributes"], "estimatedStartTime"> & {
      estimatedStartTime?: Date | null;
    };
  };
};

const VISIT_URL = `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/:workplaceId/visits/:visitId`;

export function useUpdateVisit(
  visitId: string,
  options: UseMutationOptions<Visit, AxiosError<ResponseWithError>, UpdateVisitInput> = {}
) {
  const queryClient = useQueryClient();
  const workplaceId = useCurrentUserId() ?? "";

  return useMutation({
    mutationFn: async (params) => {
      const { estimatedStartTime, ...restAttributes } = params.data.attributes;
      const apiParams: UpdateVisitRequest = {
        ...params,
        data: {
          ...params.data,
          attributes: {
            ...restAttributes,
            ...(estimatedStartTime === undefined
              ? {}
              : {
                  estimatedStartTime:
                    estimatedStartTime === null ? null : formatTime(estimatedStartTime),
                }),
          },
        },
      };
      const response = await patch({
        url: VISIT_URL.replace(":workplaceId", workplaceId).replace(":visitId", visitId),
        requestSchema: updateVisitRequestSchema,
        responseSchema: visitResponseSchema,
        data: apiParams,
      });
      return response.data.data;
    },
    onSuccess: (visit) => {
      void queryClient.invalidateQueries(
        getCaseVisitsQueryKey({
          workplaceId,
          caseId: visit.attributes.caseId,
        })
      );
      queryClient.setQueryData(getVisitQueryKey({ workplaceId, visitId: visit.id }), visit);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries(getVisitQueryKey({ workplaceId, visitId: visit.id }));
    },
    ...options,
  });
}
